import dayjs from "dayjs";
import { CalendarList, LocaleConfig } from "react-native-calendars";
import { useSelector, useDispatch } from "react-redux";
import React, { useState, useCallback, useMemo, memo, useEffect } from "react";
import {
  StyleSheet,
  View,
  TouchableOpacity,
  Pressable,
  Dimensions,
} from "react-native";

// Custom Imports
import color from "@constants/color";
import t from "@constants/localization";
import Text from "@components/CustomText";
import ToastMessage from "@components/Toast";
import BackArrow from "@assets/svg/BackArrow";
import { fontFamily } from "@utils/styles";
import { normalize } from "@constants/metrics";
import BulbIcon from "@assets/svg/Vacation/BulbIcon";
import vacationActions from "@actions/vacation.actions";
import PressableButton from "@components/PrimaryButton";
import CrossDottedLine from "@assets/svg/Vacation/CrossLine";
import { addVacations, updateVacation } from "@services/api";
import { vacationDataSelector } from "@selectors/vacation.selector";
import { VACATION_EVENTS } from "@constants/analyticsEventsCT";
import analytics from "@services/analytics";
import { vh } from "@utils/scale";
import { getMonthYear } from "@utils/getFormattedDate";
import CircularLoader from "../Story/CircularLoader";
import ModalPopup from "./components/ModalPopup";

const PATH = "screens/Vacation/index";
const Today = dayjs().format("YYYY-MM-DD");
const Tomorrow = dayjs().add(1, "day").format("YYYY-MM-DD");

const { width } = Dimensions.get("window");
const dayBoxWidth = Math.ceil(width / 7); // 7 days week into boxes

LocaleConfig.locales.hi = {
  monthNames: [
    "जनवरी",
    "फरवरी",
    "मार्च",
    "अप्रैल",
    "मई",
    "जून",
    "जुलाई",
    "अगस्त",
    "सितम्बर",
    "अक्टूबर",
    "नवम्बर",
    "दिसम्बर",
  ],
  monthNamesShort: [
    "जनवरी",
    "फरवरी",
    "मार्च",
    "अप्रैल",
    "मई",
    "जून",
    "जुलाई",
    "अगस्त",
    "सितम्बर",
    "अक्टूबर",
    "नवम्बर",
    "दिसम्बर",
  ],
  dayNames: ["रवि", "सोम", "मंगल", "बुध", "गुरु", "शुक्र", "शनि"],
  dayNamesShort: ["रवि", "सोम", "मंगल", "बुध", "गुरु", "शुक्र", "शनि"],
  today: "आज",
};

LocaleConfig.locales.en = {
  monthNames: [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ],
  monthNamesShort: [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ],
  dayNames: [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ],
  dayNamesShort: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
  today: "Today",
};

const LEAVE_STATUS = {
  PAID: "paid",
  UNPAID: "unpaid",
  NO_LEAVE: "no_leave",
  TODAY: "today",
  ALREADY_TAKEN: "already_taken",
  NOT_ALLOWED: "not_allowed",
  CANCELLED_BY_ADMIN: "cancelled_by_admin",
};

const LEAVE_TYPE = {
  PAID: "PAID",
  UNPAID: "UNPAID",
};
const calenderTheme = {
  dotColor: color.primary,
  arrowColor: color.primary,
  selectedDayBackgroundColor: color.primary,
  textDayFontWeight: "300",
  todayTextColor: color.lightBlack,
  todayBackgroundColor: color.lighterBlack,
  textMonthFontWeight: "100",
  "stylesheet.calendar.header": {
    dayHeader: {
      marginTop: 0,
      marginBottom: 0,
      width: 32,
      textAlign: "center",
      color: color.lightBlack,
      fontFamily: fontFamily.semiBold,
      fontSize: 12,
      lineHeight: 18,
    },
    monthText: {
      fontSize: 16,
      fontFamily: fontFamily.semiBold,
      color: color.mediumBlack,
    },
    header: {
      flexDirection: "row",
      justifyContent: "space-between",
      paddingLeft: 16,
      paddingRight: 16,
      alignItems: "center",
      backgroundColor: color.white,
      height: 42,
    },
    partialHeader: {
      paddingHorizontal: 16,
    },
    week: {
      marginTop: 0,
      flexDirection: "row",
      justifyContent: "space-around",
      alignItems: "center",
      backgroundColor: color.white,
      height: 40,
      borderBottomWidth: 0.5,
      borderColor: color.blank_grey,
    },
  },
  "stylesheet.day.basic": {
    today: {
      borderColor: "#48BFE3",
      borderWidth: 0.8,
    },
    todayText: {
      color: "#5390D9",
      fontWeight: "800",
    },
    container: {
      alignSelf: "center",
      alignItems: "center",
      justifyContent: "center",
    },
    base: {
      width: 50,
      height: 50,
      alignItems: "center",
    },
  },
  "stylesheet.calendar-list.main": {
    flatListContainer: {
      flex: 1,
    },
    container: {
      backgroundColor: color.white,
    },
    calendar: {
      paddingLeft: 0,
      paddingRight: 0,
    },
    staticHeader: {
      position: "absolute",
      left: 0,
      right: 0,
      top: 0,
      backgroundColor: color.white,
      paddingHorizontal: 0,
    },
  },
  "stylesheet.calendar.main": {
    dayContainer: {
      alignItems: "center",
      borderWidth: 0.5,
      borderColor: color.blank_grey,
      justifyContent: "center",
      width: dayBoxWidth,
      height: 50,
      position: "relative",
    },
    week: {
      flexDirection: "row",
      justifyContent: "space-between",
    },
    container: {
      backgroundColor: color.shadowLighter,
      flex: 1,
      paddingLeft: 0,
      paddingRight: 0,
      // width: "100%"
    },
    emptyDayContainer: {
      flex: 1,
      width: 50,
      height: 50,
      backgroundColor: color.shadowLighter,
    },
    monthView: {
      backgroundColor: color.white,
    },
  },
};

const renderHeader = (date) => {
  return (
    <View key={`${date}`} style={styles.monthHeader}>
      <Text variant="subtitle_16" newfont={true} style={styles.monthsText}>
        {getMonthYear(date)}
      </Text>
    </View>
  );
};

const langSelector = (state) => state?.auth?.user?.language;

const VacationCalendar = ({ navigation, horizontalView = false, route }) => {
  const dispatch = useDispatch();
  const { isEdit = false, vacation_date = "", type = "" } = route?.params || {};
  const { data = {}, api = {} } = useSelector(vacationDataSelector);
  const lang = useSelector(langSelector);
  const { fetched = false, pending = false } = api || {};
  LocaleConfig.defaultLocale = lang == "en" ? "en" : "hi";
  const [paidInfo, setPaidInfo] = useState(false);
  const [unpaidInfo, setUnpaidInfo] = useState(false);

  const {
    can_apply_from = "",
    can_apply_until = "",
    labels = {},
    leader_id = "",
    paid_leaves = {},
    unpaid_leaves = {},
    past_paid_leaves = [],
    no_leave_dates = [],
    terms_and_conditions = {},
    upcoming_cancelled_leaves = [],
    upcoming_leaves = [],
    past_unpaid_leaves = [],
    vacation_title = "",
  } = data;
  const checkNewUser =
    upcoming_cancelled_leaves.length === 0 &&
    upcoming_leaves.length === 0 &&
    past_paid_leaves.length === 0 &&
    past_unpaid_leaves.length === 0;

  const [modalVisible, setModalVisible] = useState(false);
  const [selectedSaledate, setSelectedSaledate] = useState("");

  useEffect(() => {
    if (!fetched) {
      dispatch(vacationActions.getVacationDetails.pending());
    }
  }, []);

  const onTouchStart = () => {
    setTimeout(() => {
      paidInfo && setPaidInfo(false);
      unpaidInfo && setUnpaidInfo(false);
    }, 150);
  };

  const [newPaidLeave, setNewPaidLeave] = useState(
    isEdit && type === LEAVE_TYPE.PAID
      ? {
          [vacation_date]: {
            background_color: color.plain_green,
            text_color: color.white,
            show_text: t(`${PATH}/paid`),
            status: LEAVE_STATUS.PAID,
            selected: false,
            marked: true,
          },
        }
      : {},
  );
  const [newUnpaidLeave, setnewUnpaidLeave] = useState(
    isEdit && type !== LEAVE_TYPE.PAID
      ? {
          [vacation_date]: {
            background_color: color.plain_amber,
            text_color: color.white,
            show_text: t(`${PATH}/unpaid`),
            status: LEAVE_STATUS.UNPAID,
            selected: false,
            marked: true,
          },
        }
      : {},
  );
  const paidCount = Object.keys(newPaidLeave).length;
  const unPaidCount = Object.keys(newUnpaidLeave).length;
  const isButtonActive = paidCount === 0 && unPaidCount === 0;

  const not_allowed = useMemo(() => {
    const obj = {};
    no_leave_dates.forEach((item) => {
      obj[item.leave_date] = {
        background_color: color.white,
        text_color: color.lightMediumBlack,
        show_text: "",
        status: LEAVE_STATUS.NOT_ALLOWED,
        selected: false,
        marked: true,
        detail_text: item.name,
      };
    });
    return obj;
  }, [no_leave_dates]);

  const cancelled_leaves = useMemo(() => {
    const obj = {};
    upcoming_cancelled_leaves.forEach((item) => {
      obj[item.vacation_date] = {
        background_color: color.plain_red,
        text_color: color.white,
        show_text: t(`${PATH}/cancelled`),
        status: LEAVE_STATUS.CANCELLED_BY_ADMIN,
        selected: false,
        marked: true,
        deleted_reason: item?.deleted_reason,
      };
    });
    return obj;
  }, [upcoming_cancelled_leaves]);

  const allPaidleaves = useMemo(() => {
    const obj = {};
    upcoming_leaves.forEach((item) => {
      if (item?.type === LEAVE_TYPE.PAID) {
        obj[item.vacation_date] = {
          background_color: color.lineColor,
          text_color: color.lightMediumBlack,
          show_text: t(`${PATH}/paid`),
          status: LEAVE_STATUS.ALREADY_TAKEN,
          selected: false,
          marked: true,
        };
      }
    });
    return obj;
  }, [upcoming_leaves]);

  const allUnpaidleaves = useMemo(() => {
    const obj = {};
    upcoming_leaves.forEach((item) => {
      if (item?.type === LEAVE_TYPE.UNPAID) {
        obj[item.vacation_date] = {
          background_color: color.lineColor,
          text_color: color.lightMediumBlack,
          show_text: t(`${PATH}/unpaid`),
          status: LEAVE_STATUS.ALREADY_TAKEN,
          selected: false,
          marked: true,
        };
      }
    });
    return obj;
  }, [upcoming_leaves]);

  const markLeaveEvents = (status) => {
    if (
      status === LEAVE_STATUS.NO_LEAVE ||
      status === LEAVE_STATUS.PAID ||
      status === LEAVE_STATUS.UNPAID
    ) {
      analytics.trackEvent(VACATION_EVENTS.MARK_LEAVE);
    }
  };

  const onDayPress = useCallback(
    (day = "", status = "", deleted_reason = "", marking = {}) => {
      console.log(day, status, marking);
      if (
        isEdit &&
        status !== LEAVE_STATUS.CANCELLED_BY_ADMIN &&
        status !== LEAVE_STATUS.TODAY &&
        status !== LEAVE_STATUS.NOT_ALLOWED
      ) {
        type === LEAVE_TYPE.PAID
          ? setNewPaidLeave({
              [day]: {
                background_color: color.plain_green,
                text_color: color.white,
                show_text: t(`${PATH}/paid`),
                status: LEAVE_STATUS.PAID,
                selected: false,
                marked: true,
              },
            })
          : setnewUnpaidLeave({
              [day]: {
                background_color: color.plain_amber,
                text_color: color.white,
                show_text: t(`${PATH}/unpaid`),
                status: LEAVE_STATUS.UNPAID,
                selected: false,
                marked: true,
              },
            });
      } else if (
        (paid_leaves?.value !== 0 || unpaid_leaves?.value !== 0) &&
        (status === LEAVE_STATUS.NO_LEAVE ||
          status === LEAVE_STATUS.PAID ||
          status === LEAVE_STATUS.UNPAID)
      ) {
        if (status === LEAVE_STATUS.PAID || paidCount !== paid_leaves?.value) {
          setNewPaidLeave((currentObj) => {
            const newOBJ = { ...currentObj };
            if (newOBJ.hasOwnProperty(day)) {
              delete newOBJ[day];
            } else {
              newOBJ[day] = {
                background_color: color.plain_green,
                text_color: color.white,
                show_text: t(`${PATH}/paid`),
                status: LEAVE_STATUS.PAID,
                selected: false,
                marked: true,
              };
            }
            return newOBJ;
          });
          markLeaveEvents(status);
        } else if (
          status === LEAVE_STATUS.UNPAID ||
          unPaidCount !== unpaid_leaves?.value
        ) {
          setnewUnpaidLeave((currentObj) => {
            const newOBJ = { ...currentObj };
            if (newOBJ.hasOwnProperty(day)) {
              delete newOBJ[day];
            } else {
              newOBJ[day] = {
                background_color: color.plain_amber,
                text_color: color.white,
                show_text: t(`${PATH}/unpaid`),
                status: LEAVE_STATUS.UNPAID,
                selected: false,
                marked: true,
              };
            }
            return newOBJ;
          });
          markLeaveEvents(status);
        } else ToastMessage("info", labels?.limit_exceed);
      } else if (status === LEAVE_STATUS.CANCELLED_BY_ADMIN) {
        ToastMessage("info", labels?.selecting_cancelled_date);
      } else if (status === LEAVE_STATUS.TODAY) {
        ToastMessage("info", labels?.selecting_before_first_date);
      } else if (status === LEAVE_STATUS.NOT_ALLOWED) {
        setSelectedSaledate(marking?.detail_text);
        setModalVisible(true);
      }
    },
    [
      newUnpaidLeave,
      upcoming_leaves,
      allUnpaidleaves,
      allPaidleaves,
      newPaidLeave,
    ],
  );

  const goBackHandler = () => {
    navigation.goBack();
  };

  const cleanUpAfterSubmit = () => {
    setNewPaidLeave({});
    setnewUnpaidLeave({});
  };

  const editVacation = async () => {
    try {
      const payload = {
        add_vacation_dates: [
          ...Object.keys(newPaidLeave),
          ...Object.keys(newUnpaidLeave),
        ],
        remove_vacation_dates: [vacation_date],
      };
      const { data } = await updateVacation(payload);
      data?.toastMessage && ToastMessage("info", data.toastMessage);
      dispatch(vacationActions.getVacationDetails.pending());
      navigation.navigate("Vacation", { toast_message: "" });
      cleanUpAfterSubmit();
    } catch (error) {
      console.log(error);
      cleanUpAfterSubmit();
    }
  };

  const onPressSubmit = async () => {
    if (isEdit) {
      editVacation();
    } else {
      try {
        const payload = {
          vacation_dates: [
            ...Object.keys(newPaidLeave),
            ...Object.keys(newUnpaidLeave),
          ],
        };
        const { data } = await addVacations(payload);
        data?.toastMessage && ToastMessage("info", data.toastMessage, 5000);
        navigation.navigate("Vacation", { toast_message: "" });
        dispatch(vacationActions.getVacationDetails.pending());
        analytics.trackEvent(VACATION_EVENTS.LEAVE_SUBMIT);
        cleanUpAfterSubmit();
      } catch (error) {
        console.log(error);
        cleanUpAfterSubmit();
      }
    }
  };

  const marked = useMemo(() => {
    return {
      [Today]: {
        background_color: color.white,
        text_color: color.mediumBlack,
        show_text: t(`${PATH}/today`),
        status: LEAVE_STATUS.TODAY,
        selected: false,
        marked: true,
      },
      [Tomorrow]: {
        background_color: color.white,
        text_color: color.mediumBlack,
        show_text: "",
        status: LEAVE_STATUS.TODAY,
        selected: false,
        marked: true,
      },
      ...allPaidleaves,
      ...not_allowed,
      ...allUnpaidleaves,
      ...cancelled_leaves,
      ...newPaidLeave,
      ...newUnpaidLeave,
    };
  }, [
    upcoming_leaves,
    no_leave_dates,
    upcoming_cancelled_leaves,
    newPaidLeave,
    newUnpaidLeave,
    cancelled_leaves,
    not_allowed,
    allPaidleaves,
    allUnpaidleaves,
  ]);

  const currentDate = () => {
    return Today;
  };

  const renderDay = useCallback(
    (dayProps) => {
      const { date, state, marking } = dayProps;
      const isDisabled = state === "disabled";
      const { dateString, timestamp, day } = date;
      const {
        background_color = color.white,
        text_color = isDisabled ? color.lightMediumBlack : color.mediumBlack,
        show_text = "",
        status = LEAVE_STATUS.NO_LEAVE,
        deleted_reason = "",
      } = marking || {};

      return (
        <TouchableOpacity
          key={dateString}
          onPress={() =>
            onDayPress(dateString, status, deleted_reason, marking)
          } // markHolidays(date)
          disabled={isDisabled}
          style={[styles.dayContainer, { backgroundColor: background_color }]}
        >
          {show_text !== "" && (
            <Text
              variant="caption_2"
              newfont={true}
              style={{
                textAlign: "center",
                position: "absolute",
                top: 2,
                color:
                  status === LEAVE_STATUS.TODAY
                    ? color.plain_green
                    : status === LEAVE_STATUS.ALREADY_TAKEN
                      ? color.lightMediumBlack
                      : text_color,
              }}
            >
              {show_text}
            </Text>
          )}
          <Text
            variant="body_text_4"
            newfont={true}
            style={{
              textAlign: "center",
              color: text_color,
              zIndex: 100,
            }}
          >
            {day}
          </Text>
          {status === LEAVE_STATUS.NOT_ALLOWED && (
            <CrossDottedLine style={styles.crossDottedLine} />
          )}
        </TouchableOpacity>
      );
    },
    [
      newUnpaidLeave,
      upcoming_leaves,
      allUnpaidleaves,
      allPaidleaves,
      newPaidLeave,
    ],
  );

  return !fetched && pending ? (
    <View style={styles.loaderStyle}>
      <CircularLoader />
    </View>
  ) : (
    <View onTouchStart={onTouchStart} style={styles.container}>
      <View style={styles.topNavContainer}>
        <TouchableOpacity onPress={goBackHandler} style={styles.arrowCont}>
          <BackArrow />
        </TouchableOpacity>
        <Text variant="entityHeading" newfont={true} style={styles.headerTitle}>
          {vacation_title || t(`${PATH}/markLeave`)}
        </Text>
      </View>
      <View style={styles.scrollView}>
        {/* <PaidUnpaid
          paid_leaves={paid_leaves}
          unpaid_leaves={unpaid_leaves}
          style={styles.paidUnpaid}
          infoText={labels?.limit_exceed}
          checkNewUser={checkNewUser}
          total_month_limit={labels?.total_month_limit}
          setPaidInfo={setPaidInfo}
          setUnpaidInfo={setUnpaidInfo}
          paidInfo={paidInfo}
          unpaidInfo={unpaidInfo}
          hideInfo={onTouchStart}
        /> */}
        <CalendarList
          style={{ marginTop: 12, flex: 1 }}
          contentContainerStyle={styles.contentContainerStyle}
          calendarStyle={styles.calenderStyle}
          theme={calenderTheme}
          markedDates={marked}
          markingType="custom"
          minDate={can_apply_from}
          maxDate={can_apply_until}
          hideExtraDays={true}
          dayComponent={renderDay}
          renderHeader={renderHeader}
          current={currentDate()}
          pastScrollRange={0}
          futureScrollRange={2}
          onDayPress={onDayPress}
          calendarHeight={vh(350)}
          horizontal={horizontalView}
          pagingEnabled={horizontalView}
          staticHeader={horizontalView}
        />
      </View>
      <View style={styles.buttonContainer}>
        {(isButtonActive || isEdit) && (
          <View style={styles.leaveInfoContainer}>
            <BulbIcon />
            <Text style={styles.infoText} variant="body_text_4" newfont={true}>
              {isEdit ? t(`${PATH}/updateInfo`) : labels?.select_date}
            </Text>
          </View>
        )}
        <PressableButton
          text={isEdit ? t(`${PATH}/update`) : t(`${PATH}/submit`)}
          buttonStyle={styles.button}
          textStyle={styles.buttonText}
          disabled={isButtonActive}
          onPress={onPressSubmit}
        />
      </View>
      <ModalPopup setVisible={setModalVisible} visible={modalVisible}>
        <View>
          <Text
            style={{
              fontFamily: fontFamily.bold,
              color: color.mediumBlack,
            }}
            variant="subtitle_16"
            newfont={true}
          >
            {t(`${PATH}/sorry`)}
          </Text>
          <Text
            style={{
              color: color.mediumBlack,
              marginTop: 4,
            }}
            variant="body_text_4"
            newfont={true}
          >
            {labels?.selecting_no_leave_date}
          </Text>
          <Text
            style={{
              color: color.lightBlack,
              marginTop: 8,
            }}
            variant="body_text_3"
            newfont={true}
          >
            {selectedSaledate}
          </Text>
        </View>
        <Pressable
          style={styles.submitModalButton}
          onPress={() => {
            setModalVisible(false);
            setSelectedSaledate("");
          }}
        >
          <Text
            style={{
              color: color.primary,
            }}
            variant="body_text_4"
            newfont={true}
          >
            {t(`${PATH}/okText`)}
          </Text>
        </Pressable>
      </ModalPopup>
    </View>
  );
};

export default memo(VacationCalendar);

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  contentContainerStyle: {
    flexGrow: 1,
  },
  topNavContainer: {
    flexDirection: "row",
    paddingVertical: normalize(18),
    paddingHorizontal: normalize(16),
    backgroundColor: color.white,
    alignItems: "center",
    elevation: 8,
    shadowColor: color.mediumBlack,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 4.65,
  },
  arrowCont: {
    marginRight: normalize(20),
  },
  headerTitle: {
    marginBottom: 0,
    paddingHorizontal: 0,
  },
  paidUnpaid: {
    marginTop: 12,
  },
  calenderStyle: {
    alignSelf: "center",
    paddingTop: 12,
  },
  dayContainer: {
    justifyContent: "center",
    alignItems: "center",
    flex: 1,
    height: "100%",
    width: "100%",
  },
  monthHeader: {
    width: "100%",
  },
  monthsText: {
    color: color.mediumBlack,
  },
  crossDottedLine: {
    position: "absolute",
    zIndex: 10,
  },
  buttonContainer: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    width: "100%",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.8,
    shadowRadius: 2,
    elevation: 15,
    zIndex: 100,
    backgroundColor: color.white,
  },
  button: {
    width: "100%",
    borderRadius: 6,
  },
  buttonText: {
    fontFamily: fontFamily.bold,
    fontSize: 16,
    lineHeight: 24,
    textAlign: "center",
  },
  leaveInfoContainer: {
    flexDirection: "row",
    marginTop: 8,
    marginBottom: 16,
    alignItems: "center",
  },
  infoText: {
    color: color.mediumBlack,
    marginStart: 9,
    lineHeight: 20,
  },
  submitModalButton: {
    alignSelf: "flex-end",
    marginTop: 23,
  },
  loaderStyle: {
    position: "absolute",
    alignSelf: "center",
    zIndex: 100,
    marginTop: vh(150),
  },
});
